import { z } from "zod";
import { fileSchema } from "../files/files.schema";
import {
  CustomPosterTextStyleAlignment,
  CustomPosterTextStyleFont,
} from "@prisma/client";

export const createUpsertCustomPosterTextStyleSchema = () => {
  return z.object({
    id: z.string().optional(),
    color: z.string(),
    fontSize: z.number(),
    font: z.nativeEnum(CustomPosterTextStyleFont),
    alignment: z.nativeEnum(CustomPosterTextStyleAlignment),
    bold: z.boolean(),
    italic: z.boolean(),
    underline: z.boolean(),
    strikeThrough: z.boolean(),
  });
};

export const createUpsertCustomPosterAddedTextSchema = () => {
  return z.object({
    id: z.string().optional(),
    style: createUpsertCustomPosterTextStyleSchema(),
    text: z.string(),
    x: z.number(),
    y: z.number(),
    w: z.number(),
  });
};

export const createUpsertCustomPosterLogoSchema = () => {
  return z.object({
    id: z.string().optional(),
    pictureId: z.string().optional(),
    picture: fileSchema.nullable(),
    x: z.number(),
    y: z.number(),
    w: z.number(),
    h: z.number(),
  });
};

export const createUpsertCustomPosterPictureSchema = () => {
  return z.object({
    id: z.string().optional(),
    picture: fileSchema,
    index: z.number(),
  });
};

export const createUpsertCustomPosterSchema = () => {
  return z.object({
    id: z.string().optional(),
    format: z.enum(["format4x3", "format16x9", "format21x9"]),
    x: z.number(),
    y: z.number(),
    w: z.number(),
    h: z.number(),
    selectedPictureIndex: z.number(),
    pictures: z.array(createUpsertCustomPosterPictureSchema()),
    addedTexts: z.array(createUpsertCustomPosterAddedTextSchema()),
    customPosterLogoId: z.string().optional(),
    logos: z.array(createUpsertCustomPosterLogoSchema()),
    eventId: z.string().optional(),
    posterRender: fileSchema,
  });
};

export type UpsertCustomPosterTextStyleSchema = ReturnType<
  typeof createUpsertCustomPosterTextStyleSchema
>;
export type UpsertCustomPosterAddedTextSchema = ReturnType<
  typeof createUpsertCustomPosterAddedTextSchema
>;
export type UpsertCustomPosterLogoSchema = ReturnType<
  typeof createUpsertCustomPosterLogoSchema
>;
export type UpsertCustomPosterPictureSchema = ReturnType<
  typeof createUpsertCustomPosterPictureSchema
>;
export type UpsertCustomPosterSchema = ReturnType<
  typeof createUpsertCustomPosterSchema
>;

export type UpsertCustomPosterTextStyleForm =
  z.infer<UpsertCustomPosterTextStyleSchema>;
export type UpsertCustomPosterAddedTextForm =
  z.infer<UpsertCustomPosterAddedTextSchema>;
export type UpsertCustomPosterLogoForm = z.infer<UpsertCustomPosterLogoSchema>;
export type UpsertCustomPosterPictureForm =
  z.infer<UpsertCustomPosterPictureSchema>;
export type UpsertCustomPosterForm = z.infer<UpsertCustomPosterSchema>;

export const defaultUpsertCustomPosterTextStyleValues: UpsertCustomPosterTextStyleForm =
  {
    color: "",
    fontSize: 5,
    alignment: "left",
    font: "anton",
    bold: false,
    italic: false,
    underline: false,
    strikeThrough: false,
  };

export const defaultUpsertCustomPosterAddedTextValues: UpsertCustomPosterAddedTextForm =
  {
    style: defaultUpsertCustomPosterTextStyleValues,
    text: "",
    x: 0,
    y: 0,
    w: 0,
  };

export const defaultUpsertCustomPosterLogoValues: UpsertCustomPosterLogoForm = {
  picture: null,
  x: 0,
  y: 0,
  w: 0,
  h: 0,
};

export const defaultUpsertCustomPosterPictureValues: UpsertCustomPosterPictureForm =
  {
    picture: {
      key: "",
      name: "",
      type: "media" as const,
    },
    index: 0,
  };

export const defaultUpsertCustomPosterValues: UpsertCustomPosterForm = {
  format: "format4x3",
  x: 0,
  y: 0,
  w: 100,
  h: 100,
  selectedPictureIndex: 0,
  pictures: [],
  addedTexts: [],
  logos: [],
  eventId: "",
  posterRender: {
    key: "",
    name: "",
    type: "media" as const,
  },
};
